import { useState, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarDays, Euro, CheckCircle2, Truck, User, Mail, Phone, ArrowRight, ArrowLeft } from "lucide-react";
import { useLanguage, useT } from "@/contexts/LanguageContext";
import { de, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingDialog({ vehicle, open, onOpenChange, onComplete }) {
  const { lang } = useLanguage();
  const t = useT();
  const [step, setStep] = useState(1);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const minDate = vehicle?.available_from ? new Date(vehicle.available_from + "T00:00:00") : new Date();
  const effectiveMinDate = minDate < new Date() ? new Date() : minDate;

  const totalDays = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return 0;
    return Math.max(Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)), 1);
  }, [dateRange]);

  const totalPrice = useMemo(() => vehicle ? totalDays * vehicle.daily_rate : 0, [totalDays, vehicle]);

  const handleNextStep = () => {
    if (step === 1 && (!dateRange.from || !dateRange.to)) { toast.error(t("booking_date_error")); return; }
    if (step === 2 && (!form.name || !form.email || !form.phone)) { toast.error(t("booking_form_error")); return; }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        vehicle_id: vehicle.id,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        start_date: dateRange.from.toISOString().split("T")[0],
        end_date: dateRange.to.toISOString().split("T")[0],
      });
      setBookingResult(res.data);
      setStep(4);
      toast.success(t("booking_created"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("booking_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setDateRange({ from: undefined, to: undefined });
    setForm({ name: "", email: "", phone: "" });
    setBookingResult(null);
    onOpenChange(false);
    if (bookingResult) onComplete();
  };

  const formatDateLocale = (d) => {
    if (!d) return "-";
    return d.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  if (!vehicle) return null;

  const vehicleTypeLabel = vehicle.vehicle_type === "lkw" ? t("booking_lkw") : t("booking_transporter");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg rounded-sm p-0 overflow-hidden" data-testid="booking-dialog">
        <div className="bg-[#002FA7] text-white p-5">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {step === 4 ? t("booking_confirmed") : vehicle.name}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-sm">
              {step === 4 ? t("booking_thank_you") : `${vehicle.weight_class} ${vehicleTypeLabel} · ${vehicle.daily_rate.toFixed(0)} EUR/${lang === "de" ? "Tag" : "day"}`}
            </DialogDescription>
          </DialogHeader>
          {step < 4 && (
            <div className="flex items-center gap-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${s <= step ? "bg-[#FFD700] text-black" : "bg-white/20 text-white/50"}`}>{s}</div>
                  {s < 3 && <div className={`w-8 h-0.5 ${s < step ? "bg-[#FFD700]" : "bg-white/20"}`} />}
                </div>
              ))}
              <span className="text-xs text-white/50 ml-2">
                {step === 1 ? t("booking_step_date") : step === 2 ? t("booking_step_data") : t("booking_step_confirm")}
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          {step === 1 && (
            <div data-testid="booking-step-dates">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="w-4 h-4 text-[#002FA7]" />
                <span className="text-sm font-medium text-[#0A0A0A]">{t("booking_select_period")}</span>
              </div>
              <div className="flex justify-center">
                <Calendar mode="range" selected={dateRange} onSelect={(range) => setDateRange(range || { from: undefined, to: undefined })} locale={lang === "de" ? de : enUS} numberOfMonths={1} disabled={{ before: effectiveMinDate }} className="rounded-sm border border-border" data-testid="booking-calendar" />
              </div>
              {totalDays > 0 && (
                <div className="mt-4 p-3 bg-[#F3F5F7] border border-border flex items-center justify-between" data-testid="price-preview">
                  <div>
                    <div className="text-xs text-[#475569]">{formatDateLocale(dateRange.from)} - {formatDateLocale(dateRange.to)}</div>
                    <div className="text-sm text-[#0A0A0A]">{totalDays} {totalDays === 1 ? t("booking_day") : t("booking_days")} x {vehicle.daily_rate.toFixed(0)} EUR</div>
                  </div>
                  <div className="text-xl font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totalPrice.toFixed(2)} EUR</div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="booking-step-customer">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-[#002FA7]" />
                <span className="text-sm font-medium text-[#0A0A0A]">{t("booking_your_data")}</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("booking_name")}</label>
                <Input data-testid="booking-name-input" placeholder={t("booking_name_placeholder")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("booking_email")}</label>
                <Input data-testid="booking-email-input" type="email" placeholder={t("booking_email_placeholder")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-1.5 block">{t("booking_phone")}</label>
                <Input data-testid="booking-phone-input" type="tel" placeholder={t("booking_phone_placeholder")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-step-summary">
              <div className="space-y-4">
                <div className="p-4 bg-[#F3F5F7] border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#002FA7]" /><span className="text-sm font-medium">{vehicle.name}</span></div>
                    <span className="text-xs text-[#475569]">{vehicle.weight_class}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#475569]" /><span>{formatDateLocale(dateRange.from)} - {formatDateLocale(dateRange.to)}</span></div>
                    <span>{totalDays} {totalDays === 1 ? t("booking_day") : t("booking_days")}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-[#475569]" /><span>{form.name}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-[#475569]" /><span>{form.email}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-[#475569]" /><span>{form.phone}</span></div>
                </div>
                <div className="border-t border-border pt-4 flex items-center justify-between">
                  <div className="text-sm text-[#475569]">{t("booking_total")}</div>
                  <div className="text-2xl font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{totalPrice.toFixed(2)} EUR</div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <Euro className="w-3.5 h-3.5 inline mr-1.5" />{t("booking_payment_note")}
                </div>
              </div>
            </div>
          )}

          {step === 4 && bookingResult && (
            <div className="text-center py-4" data-testid="booking-step-success">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
              <h3 className="text-lg font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t("booking_success_title")}</h3>
              <p className="text-sm text-[#475569] mb-6">{t("booking_success_text")}</p>
              <div className="text-left p-4 bg-[#F3F5F7] border border-border space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#475569]">{t("booking_nr")}</span><span className="font-mono text-xs">{bookingResult.id.slice(0, 12)}...</span></div>
                <div className="flex justify-between"><span className="text-[#475569]">{t("booking_vehicle")}</span><span className="font-medium">{bookingResult.vehicle_name}</span></div>
                <div className="flex justify-between"><span className="text-[#475569]">{t("booking_period")}</span><span>{bookingResult.total_days} {bookingResult.total_days === 1 ? t("booking_day") : t("booking_days")}</span></div>
                <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="text-[#475569]">{t("booking_total")}</span><span className="font-black text-[#002FA7]">{bookingResult.total_price.toFixed(2)} EUR</span></div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            {step > 1 && step < 4 ? (
              <Button variant="ghost" className="text-sm text-[#475569] hover:text-[#0A0A0A]" onClick={() => setStep(step - 1)} data-testid="booking-back-button">
                <ArrowLeft className="w-4 h-4 mr-1" />{t("booking_back")}
              </Button>
            ) : <div />}
            {step < 3 && (
              <Button onClick={handleNextStep} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white rounded-sm text-sm font-semibold" data-testid="booking-next-button">
                {t("booking_next")}<ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 rounded-sm text-sm font-semibold" data-testid="booking-submit-button">
                {submitting ? t("booking_submitting") : t("booking_submit")}<CheckCircle2 className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === 4 && (
              <Button onClick={handleClose} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white rounded-sm text-sm font-semibold w-full" data-testid="booking-close-button">
                {t("booking_close")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
